import AntIcon from "@/components/icons/AntIcon";
import { selectStandardProps } from "@/utils/componentUtils";
import { validations } from "@/utils/validationUtils";
import { Flex, Form, Input, message, Modal, Select, Tooltip, type InputRef } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetAgentPartnerIdNumber } from "../../../../common/api/queries";
import { ModalSizes } from "../../../../common/constants";
import { useIsCurrentAgentTypePlus } from "../../../auth/hooks";

const LABEL_TEXT_WIDTH = 105;

type PrefixIDNumbers = {
  1: string;
  3: string;
  5: string;
};

const EMPTY_PREFIX_ID_NUMBERS: PrefixIDNumbers = {
  1: "",
  3: "",
  5: "",
};

export const AgentPartnerIdNumber = () => {
  const { t } = useTranslation();

  const ref = useRef<InputRef>(null);
  const [actionsWidth, setActionsWidth] = useState(0);
  const [isOpenIdNumberPrefixModal, setIsOpenIdNumberPrefixModal] = useState(false);
  const [isLoadingPartnerIdNumbers, setIsLoadingPartnerIdNumbers] = useState(false);
  const [prefixIdNumbers, setPrefixIdNumbers] = useState<PrefixIDNumbers>(() => ({ ...EMPTY_PREFIX_ID_NUMBERS }));

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const [messageApi, contextHolder] = message.useMessage();
  const form = Form.useFormInstance();
  const [selectPartnerIdNumberForm] = Form.useForm<{ partnerIdNumber: string }>();

  const { mutateAsync: getAgentPartnerIdNumber } = useGetAgentPartnerIdNumber();

  useEffect(() => {
    const inputElement = ref.current?.input;

    if (!inputElement) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      const elementWidth = inputElement.offsetWidth;

      if (elementWidth) {
        setActionsWidth(elementWidth - LABEL_TEXT_WIDTH);
      }
    });

    resizeObserver.observe(inputElement);

    return () => resizeObserver.disconnect();
  }, []);

  const openIdNumberPrefixModal = () => {
    selectPartnerIdNumberForm.resetFields();
    setIsOpenIdNumberPrefixModal(true);
    setPrefixIdNumbers({ ...EMPTY_PREFIX_ID_NUMBERS });
    setIsLoadingPartnerIdNumbers(true);

    void Promise.allSettled([
      getAgentPartnerIdNumber({ idNumberPrefix: 1 }),
      getAgentPartnerIdNumber({ idNumberPrefix: 3 }),
      getAgentPartnerIdNumber({ idNumberPrefix: 5 }),
    ]).then(([numberPrefixOne, numberPrefixThree, numberPrefixFive]) => {
      setIsLoadingPartnerIdNumbers(false);

      if (
        numberPrefixOne.status === "fulfilled" &&
        numberPrefixThree.status === "fulfilled" &&
        numberPrefixFive.status === "fulfilled"
      ) {
        setPrefixIdNumbers({
          1: numberPrefixOne.value.startsWith("1") ? numberPrefixOne.value : "",
          3: numberPrefixThree.value.startsWith("3") ? numberPrefixThree.value : "",
          5: numberPrefixFive.value.startsWith("5") ? numberPrefixFive.value : "",
        });
      }
    });
  };

  const closeIdNumberPrefixModal = () => {
    setIsLoadingPartnerIdNumbers(false);
    setIsOpenIdNumberPrefixModal(false);
  };

  const processPartnerIdNumber = (partnerIdNumber: string) => {
    if (partnerIdNumber?.length) {
      form.setFieldsValue({ partnerIdNumber: partnerIdNumber });
    } else {
      messageApi.info(t("agent.helpers.errorInfoPartnerIdNumber"), 5);
    }
  };

  const renderLabel = () => (
    <Flex style={{ width: "100%" }} justify="space-between">
      <span style={{ width: LABEL_TEXT_WIDTH }}>{t("agent.attrs.partnerIdNumber")}</span>

      <span style={{ width: actionsWidth, textAlign: "right", overflow: "hidden" }}>
        <Tooltip title={t("agent.helpers.generatePartnerIdNumberHint")}>
          <span>
            <AntIcon
              type="id-card"
              color="green"
              onClick={async () => {
                if (isTopAgentPlus) {
                  openIdNumberPrefixModal();
                } else {
                  const data = await getAgentPartnerIdNumber(undefined);

                  processPartnerIdNumber(data);
                }
              }}
            />
          </span>
        </Tooltip>
      </span>
    </Flex>
  );

  const handleSubmitModalForm = () => {
    selectPartnerIdNumberForm.validateFields().then((values) => {
      processPartnerIdNumber(values.partnerIdNumber);
      closeIdNumberPrefixModal();
    });
  };

  return (
    <>
      {contextHolder}

      <Form.Item name="partnerIdNumber" label={renderLabel()} rules={[validations.size(1, 64), validations.numeric]}>
        <Input ref={ref} />
      </Form.Item>

      <Modal
        width={ModalSizes.TINY}
        loading={isLoadingPartnerIdNumbers}
        title={t("agent.titles.selectNumberPrefix")}
        open={isOpenIdNumberPrefixModal}
        onOk={handleSubmitModalForm}
        onCancel={closeIdNumberPrefixModal}
        afterClose={() => selectPartnerIdNumberForm.resetFields()}
        mask={{ closable: false }}
      >
        <Form layout="vertical" form={selectPartnerIdNumberForm} name="selectPartnerIdNumberForm">
          <Form.Item name="partnerIdNumber" label={t("agent.attrs.partnerIdNumber")} rules={[validations.notBlank]}>
            <Select
              {...selectStandardProps}
              options={Object.entries(prefixIdNumbers).map(([key, value]) => ({
                value,
                label: `${t("agent.helpers.idNumberPrefix")} ${key}: ${value.length ? value : t("agent.helpers.fullIdNumberPrefix")}`,
                disabled: !value,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
